import React from 'react';
import SVGIcon from '../SVGIcon/SVGIcon';
import {
  Segment,
  SegmentedFrame,
  SegmentIcon,
  SegmentLabel
} from './SegmentedControl.style';

export interface SegmentedControlOption<Value extends string> {
  id: Value;
  label: string;
  /** The glyph the option is shown as where the label does not fit; needed on
   *  every option when the control is asked to collapse below `md` */
  icon?: string;
}

interface SegmentedControlProps<Value extends string> {
  options: SegmentedControlOption<Value>[];
  /** The one option that is active: they are mutually exclusive */
  value: Value;
  onChange: (value: Value) => void;
  /** What the choice is, for screen readers — "View mode", say */
  label: string;
  /** Below `md`, show the options as their glyphs only */
  collapseOnMobile?: boolean;
}

/**
 * A choice between a handful of mutually exclusive options, of which exactly
 * one is active — a view mode, a unit, a range. It is one control rather than
 * a row of buttons: the options sit in a shared frame, and picking one makes
 * it the active one instead of firing an action of its own.
 */
const SegmentedControl = <Value extends string>({
  options,
  value,
  onChange,
  label,
  collapseOnMobile = false
}: SegmentedControlProps<Value>) => (
  <SegmentedFrame role="radiogroup" aria-label={label} data-test-segmented-control>
    {options.map((option) => (
      <Segment
        key={option.id}
        type="button"
        role="radio"
        aria-checked={option.id === value}
        aria-label={collapseOnMobile ? option.label : undefined}
        $active={option.id === value}
        onClick={() => onChange(option.id)}
        data-test-segment={option.id}
      >
        {collapseOnMobile && option.icon && (
          <SegmentIcon>
            <SVGIcon icon={option.icon} size="inherit" />
          </SegmentIcon>
        )}
        <SegmentLabel $collapses={collapseOnMobile && Boolean(option.icon)}>
          {option.label}
        </SegmentLabel>
      </Segment>
    ))}
  </SegmentedFrame>
);

export default SegmentedControl;
